import {DestroyRef, inject, Injectable, Injector} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router, TitleStrategy, RouterStateSnapshot} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {merge} from 'rxjs';
import {PageTitleService} from './page-title.service';

/**
 * After each navigation, resolves the route `title` as an i18n key via `TranslateService`,
 * then applies {@link PageTitleService.formatWithAppName}.
 * Route configs should use keys from `PAGE_TITLE_KEYS` (e.g. `pageTitle.overview`).
 *
 * Do not inject `Router` at field level: the router pulls in `TitleStrategy` during its
 * construction, which would create a circular dependency. Resolve `Router` lazily via `Injector`.
 */
@Injectable()
export class AssociateTitleStrategy extends TitleStrategy {
  private readonly pageTitleService = inject(PageTitleService);
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();
    merge(this.translate.onLangChange, this.translate.onTranslationChange)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const router = this.injector.get(Router);
        this.updateTitle(router.routerState.snapshot);
      });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const routeTitleKey = this.buildTitle(snapshot);
    if (routeTitleKey !== undefined && routeTitleKey !== '') {
      const pagePart = this.translate.instant(routeTitleKey);
      this.pageTitleService.setFromSegments([pagePart]);
    } else {
      this.pageTitleService.resetToAppDefault();
    }
  }
}
